/**
 * [화면 편집] 의 문서 다루기 — 편집 중인 iframe 문서의 요소를 고르고 적는 일(2026-10-30).
 *
 * 문서는 스크립트를 끈 샌드박스라 이 안의 리스너는 불리지 않는다(WebKit). 그래서 여기 함수들은 부모가 문서를 들여다보고
 * 고치기만 한다. 스타일은 늘 글자로 고친다(`setInline` → `patchStyle`) — CSSOM 으로 쓰면 옛 웹뷰가 읽던 접두 속성이 빠진다.
 */
import { VIDEO_ATTR } from '@signageone/core';
import { descendFrame, patchStyle, sameBox, textEditable, type Box } from './model.js';

export const SVG_NS = 'http://www.w3.org/2000/svg';

/** 레이어로 보이지 않는 것 — 화면에 그려지지 않거나 고칠 거리가 아닌 태그. */
const SKIP = new Set(['SCRIPT', 'STYLE', 'LINK', 'META', 'TITLE', 'HEAD', 'NOSCRIPT', 'TEMPLATE', 'BR', 'WBR', 'BASE']);
/** 안을 들여다보지 않는 것 — 그 자체가 한 장의 그림·입력이다. */
const LEAF = new Set([
  'IMG',
  'SVG',
  'VIDEO',
  'CANVAS',
  'IFRAME',
  'INPUT',
  'TEXTAREA',
  'SELECT',
  'PICTURE',
  'OBJECT',
  'EMBED',
]);

/** 그림 영역 — 자리를 잡아 두고 그림은 나중에 고르는 틀(`components.ts`). 값은 그림 맞춤(cover·contain). */
export const IMAGE_FRAME_ATTR = 'data-so-image';

export function isImageFrame(element: Element): boolean {
  return element.hasAttribute(IMAGE_FRAME_ATTR);
}

/** D-day — 기준일까지 남은 날·지난 날을 그리는 글(`components.ts`). 값은 기준일(YYYY-MM-DD). 글은 계산으로만 바뀐다. */
export const DDAY_ATTR = 'data-so-dday';

export function isDday(element: Element): boolean {
  return element.hasAttribute(DDAY_ATTR);
}

/**
 * 영상 — 콘텐츠로 올린 영상을 가리키는 `<video data-so-video="<자산 id>">`(2026-10-30, `@signageone/core` 의 `html-media.ts`).
 * 문서에는 주소가 없다 — 화면은 받아 둔 파일을 칸 안으로 넘기고, 편집 화면은 콘솔의 미리보기 주소를 잠깐 단다(저장에는 빠진다).
 */
export function isVideo(element: Element): boolean {
  return element.tagName.toLowerCase() === 'video' && element.hasAttribute(VIDEO_ATTR);
}

/** 편집기가 문서에 남기는 잠금 표시 — 그려지는 데는 아무 영향이 없다(저장한 문서에 남아 다음에 열어도 잠겨 있다). */
export const LOCK_ATTR = 'data-so-lock';

export function setInline(element: Element, changes: Readonly<Record<string, string | null>>): void {
  const next = patchStyle(element.getAttribute('style'), changes);
  if (next) element.setAttribute('style', next);
  else element.removeAttribute('style');
}

export function boxOf(element: Element): Box {
  const rect = element.getBoundingClientRect();
  return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
}

const tagOf = (element: Element): string => element.tagName.toUpperCase();

export function isLeafElement(element: Element): boolean {
  return LEAF.has(tagOf(element)) || element.namespaceURI === SVG_NS;
}

/** 레이어 나무의 자식 — 그려지는 요소만, 그림(svg 등) 안은 들여다보지 않는다. */
export function layerChildren(element: Element): HTMLElement[] {
  if (isLeafElement(element)) return [];
  return Array.from(element.children).filter((child) => !SKIP.has(tagOf(child))) as HTMLElement[];
}

/** 몸통에서 이 요소까지의 자리(자식 차례) — 되돌리기로 문서를 새로 써도 같은 요소를 다시 찾는다. */
export function pathOf(root: Element, element: Element): number[] | null {
  const path: number[] = [];
  let current: Element | null = element;
  while (current && current !== root) {
    const parent: Element | null = current.parentElement;
    if (!parent) return null;
    const index = layerChildren(parent).indexOf(current as HTMLElement);
    if (index < 0) return null;
    path.unshift(index);
    current = parent;
  }
  return current === root ? path : null;
}

export function atPath(root: Element, path: readonly number[]): HTMLElement | null {
  let current: Element | undefined = root;
  for (const index of path) {
    current = layerChildren(current)[index];
    if (!current) return null;
  }
  return current as HTMLElement;
}

export const pathKey = (path: readonly number[]): string => path.join('.');

/** 문서의 틀 — 고르지 않는 바탕이고, 새로 넣는 것이 붙는 곳이다(`descendFrame`). */
export function rootFrame(document_: Document, viewport: Box): HTMLElement {
  return descendFrame<HTMLElement>(document_.body, layerChildren, boxOf, viewport);
}

export function isLocked(element: Element): boolean {
  return !!element.closest(`[${LOCK_ATTR}]`);
}

/** 고를 수 있는 것 — 그림 안의 선은 그림 전체로, 몸통·뼈대는 아니다. */
export function pickable(document_: Document, element: Element | null): HTMLElement | null {
  let current = element;
  if (!current) return null;
  while (current.parentElement && current.parentElement.namespaceURI === SVG_NS) current = current.parentElement;
  if (current === document_.documentElement || current === document_.body || SKIP.has(tagOf(current))) return null;
  return current as HTMLElement;
}

/** 그 자리의 가장 안쪽 요소 — 잠긴 것은 건너뛰고 그 밑의 것을 본다. */
export function deepestAt(document_: Document, point: { x: number; y: number }): HTMLElement | null {
  const stack = document_.elementsFromPoint(point.x, point.y);
  for (const element of stack) {
    const pick = pickable(document_, element);
    if (pick && !isLocked(pick)) return pick;
  }
  return null;
}

/** 고르지 않는 것 — 문서의 틀과 그 바깥(몸통·뼈대). 누르면 빈 곳을 누른 것으로 본다. */
export function backdropOf(frame: HTMLElement): Set<Element> {
  const out = new Set<Element>();
  for (let element: Element | null = frame; element; element = element.parentElement) out.add(element);
  return out;
}

/**
 * 누른 자리에서 고를 것 — **보이는 그대로 하나**(쉬운 판). 가장 안쪽 요소에서 시작해, 글 덩이 안의 조각(굵게·기울임·
 * 숫자만 감싼 span 등)이면 그 글 덩이까지 올라간다. 그래서 글을 누르면 그 글, 그림을 누르면 그 그림, 상자의 빈 곳을
 * 누르면 그 상자가 골린다. 그림 영역 안이면 그 틀. 틀과 그 바깥이면 null.
 */
export function pickTarget(deepest: HTMLElement | null, backdrop: ReadonlySet<Element>, view: Window): HTMLElement | null {
  if (!deepest || backdrop.has(deepest)) return null;
  // 그림 영역 안(그림·안내 글)을 누르면 그 틀 하나.
  const frame = deepest.closest(`[${IMAGE_FRAME_ATTR}]`) as HTMLElement | null;
  if (frame && !backdrop.has(frame)) return frame;
  let current = deepest;
  for (let parent = current.parentElement; parent && !backdrop.has(parent); parent = current.parentElement) {
    if (!isTextLayer(parent, view)) break;
    current = parent;
  }
  return current;
}

/** [감싼 상자 고르기] — 한 층 바깥. 꼭 맞게 감싼 껍데기는 골라도 달라 보이지 않으니 건너뛴다. 틀에 닿으면 null. */
export function parentTarget(element: HTMLElement, backdrop: ReadonlySet<Element>): HTMLElement | null {
  const box = boxOf(element);
  for (let parent = element.parentElement; parent && !backdrop.has(parent); parent = parent.parentElement) {
    if (!sameBox(box, boxOf(parent))) return parent;
  }
  return null;
}

/** 글을 바로 고칠 수 있는 층 — 글이 있고, 안에 든 것이 다 글줄 속 요소(굵게·기울임 등)뿐이다. */
export function isTextLayer(element: HTMLElement, view: Window): boolean {
  if (!textEditable(element.tagName) || !element.textContent?.trim() || isImageFrame(element) || isDday(element))
    return false;
  return layerChildren(element).every(
    (child) => !isLeafElement(child) && view.getComputedStyle(child).display.startsWith('inline'),
  );
}

export function isHidden(element: Element, view: Window): boolean {
  const style = view.getComputedStyle(element);
  return style.visibility === 'hidden' || style.display === 'none';
}

/**
 * 요소를 흐름에서 떼어 문서의 한 자리(`box`, 문서 좌표)에 둔다 — 복제·붙여넣기·새 상자. 감싼 상자의 자리 기준을 몰라도 되게,
 * 먼저 왼쪽 위 0 에 놓고 재서 그만큼 옮긴다.
 */
export function placeAbsolute(element: HTMLElement, box: Box): void {
  setInline(element, {
    position: 'absolute',
    left: '0px',
    top: '0px',
    right: null,
    bottom: null,
    margin: '0',
    'box-sizing': 'border-box',
    width: `${Math.round(box.width)}px`,
    height: `${Math.round(box.height)}px`,
    'max-width': 'none',
  });
  const origin = boxOf(element);
  setInline(element, { left: `${Math.round(box.x - origin.x)}px`, top: `${Math.round(box.y - origin.y)}px` });
}

/** 자리를 가진 요소의 겹침 차례 — `auto` 는 0 으로 본다. */
export function zIndexOf(element: Element, view: Window): number {
  const value = Number.parseInt(view.getComputedStyle(element).zIndex, 10);
  return Number.isFinite(value) ? value : 0;
}
